import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLibrary } from '../lib/Library';

/**
 * Title details fetched ahead of a click, keyed `type:id`.
 *
 * Shared across every card so hovering the same title twice costs one request,
 * and capped because a long browse session would otherwise hold every detail
 * ever peeked at. Failures resolve to null and are simply previewed without the
 * extra fields.
 */
const prefetched = new Map();
const PREFETCH_CAP = 80;

export const prefetchTitle = (type, id) => {
  const key = `${type}:${id}`;
  if (prefetched.has(key)) return prefetched.get(key);

  const promise = axios
    .get(`/api/catalog/title/${type}/${id}`)
    .then(({ data }) => data.data)
    .catch(() => null);

  if (prefetched.size >= PREFETCH_CAP) {
    prefetched.delete(prefetched.keys().next().value);
  }
  prefetched.set(key, promise);
  return promise;
};

// How long a pointer has to rest on a card before it means "tell me more"
// rather than "passing through on the way to another card".
const HOVER_DELAY_MS = 400;
// And how long the preview lingers once the pointer leaves, so crossing the
// gap between card and preview doesn't close it.
const LINGER_MS = 140;

/**
 * Open a title as a modal over whatever page is showing.
 *
 * The page underneath keeps rendering — and its scroll position — because the
 * router is told to draw the page for the *background* location and the title
 * on top. From inside one modal, the next one keeps the same background rather
 * than stacking modals on modals.
 */
export const useOpenTitle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (title, state = {}) =>
    navigate(`/title/${title.type}/${title.id}`, {
      state: { background: location.state?.background || location, ...state }
    });
};

/** "2 seasons" / "1h 47m" — the one fact that says how long this is. */
const lengthOf = (title) => {
  if (title.type === 'series') {
    const n = title.seasonCount ?? title.seasons?.length;
    return n ? `${n} season${n === 1 ? '' : 's'}` : null;
  }
  return title.runtime || null;
};

/**
 * The card used by every rail and grid, and the preview that opens when a
 * pointer rests on it: art, the two actions that matter, and enough meta to
 * decide without a round trip through the title page per maybe.
 *
 * Landscape (16:9, the title's backdrop) is the default shape; `poster` keeps
 * the 2:3 sheet for the places that want it. The preview is a portal on
 * document.body — the rails clip their own overflow (they scroll), so anything
 * expanding in place would be cut off at the shelf edge. Touch screens never
 * hover and keep the plain tap-through behaviour.
 */
const PosterCard = ({ title, variant = 'rail', shape = 'landscape', caption = null, badge = null }) => {
  const navigate = useNavigate();
  const openTitle = useOpenTitle();
  const { has, toggle, ratingFor, rate } = useLibrary();
  const [preview, setPreview] = useState(null); // {left, top, width} once open
  const [detail, setDetail] = useState(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const anchor = useRef(null);

  const enter = () => {
    // Warm the cache immediately — it also makes the modal instant — but only
    // show the panel once the pointer has settled.
    prefetchTitle(title.type, title.id).then((data) => data && setDetail(data));

    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;

      const vw = window.innerWidth;
      const width = Math.min(Math.max(r.width * 1.45, 320), 420);
      const imgH = (width * 9) / 16;
      const total = imgH + 138;
      let left = r.left + r.width / 2 - width / 2;
      left = Math.max(12, Math.min(left, vw - width - 12));
      let top = r.top + r.height / 2 - imgH / 2;
      top = Math.max(76, Math.min(top, window.innerHeight - total - 12));
      setPreview({ left, top, width });
    }, HOVER_DELAY_MS);
  };

  const leave = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setPreview(null), LINGER_MS);
  };

  const close = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setPreview(null);
  };

  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    []
  );

  // The panel is positioned against a rect that scrolling invalidates, so any
  // scroll closes it rather than letting it drift over the wrong card.
  useEffect(() => {
    if (!preview) return undefined;
    const onScroll = () => setPreview(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [preview]);

  const shown = detail || title;
  const inList = has(title.type, title.id);
  const loved = (ratingFor(title.type, title.id) || 0) >= 5;
  const landscape = shape === 'landscape';
  const art = landscape ? shown.backdrop || title.backdrop : title.poster;

  const open = (event) => {
    event?.preventDefault();
    close();
    openTitle(title);
  };

  const play = (event) => {
    event.stopPropagation();
    close();
    navigate(`/title/${title.type}/${title.id}`, { state: { autoplay: true } });
  };

  return (
    <>
      <a
        href={`/title/${title.type}/${title.id}`}
        ref={anchor}
        className={`card card--${landscape ? 'wide' : 'poster'}${variant === 'grid' ? ' card--grid' : ''}`}
        onClick={open}
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        <span className="card-art">
          {art ? (
            <img src={art} alt="" loading="lazy" />
          ) : (
            <span className="art-label">{landscape ? 'TITLE ART 16:9' : 'POSTER 2:3'}</span>
          )}
          {badge && <span className="card-badge">{badge}</span>}
          {/* The name goes on the art: on a backdrop it is the title treatment,
              and with no artwork at all the plate would be anonymous. */}
          {(landscape || !art) && <span className="card-name">{title.name}</span>}
        </span>
        {caption && <span className="card-caption">{caption}</span>}
      </a>

      {preview &&
        createPortal(
          <div
            className="pv"
            style={{ left: preview.left, top: preview.top, width: preview.width }}
            onMouseEnter={() => clearTimeout(closeTimer.current)}
            onMouseLeave={leave}
            onClick={open}
          >
            <div className="pv-art">
              {(shown.backdrop || shown.poster) && <img src={shown.backdrop || shown.poster} alt="" />}
              <span className="pv-fade" />
              <span className="pv-name">{shown.name}</span>
            </div>
            <div className="pv-body">
              <div className="pv-actions">
                <button type="button" className="pv-play" aria-label="Play" onClick={play}>
                  <span className="play-tri play-tri--dark" />
                </button>
                <button
                  type="button"
                  className={`pv-ring${inList ? ' is-on' : ''}`}
                  aria-label={inList ? 'Remove from my list' : 'Add to my list'}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(shown);
                  }}
                >
                  <span className={`plus${inList ? ' plus--check' : ''}`} />
                </button>
                <button
                  type="button"
                  className={`pv-ring${loved ? ' is-on' : ''}`}
                  aria-label={loved ? 'Unmark as loved' : 'Mark as loved'}
                  onClick={(event) => {
                    event.stopPropagation();
                    rate(shown, 5);
                  }}
                >
                  {loved ? '♥' : '♡'}
                </button>
                <button type="button" className="pv-ring pv-more" aria-label="More info" onClick={open}>
                  <span className="chev" />
                </button>
              </div>
              <div className="pv-meta">
                {shown.rating ? <span className="pv-star">★ {shown.rating}</span> : null}
                {shown.year ? <span>{shown.year}</span> : null}
                {shown.certification ? <span className="cert">{shown.certification}</span> : null}
                {lengthOf(shown) ? <span>{lengthOf(shown)}</span> : null}
                <span className="cert cert--hd">HD</span>
              </div>
              {shown.genres?.length ? <div className="pv-genres">{shown.genres.slice(0, 3).join(' · ')}</div> : null}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PosterCard;
